import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useToast } from '@/components/ui/use-toast';

export interface PaymentPlan {
  id: string;
  user_id: string;
  bill_id: string;
  customer_id: string;
  plan_type: 'installment' | 'deferred' | 'partial' | 'custom';
  total_amount: number;
  installments: any[];
  status: 'pending' | 'active' | 'completed' | 'defaulted' | 'cancelled';
  start_date: string;
  end_date?: string;
  terms: any;
  created_at: string;
  updated_at: string;
}

export interface CreatePaymentPlanData {
  bill_id: string;
  customer_id: string;
  plan_type: 'installment' | 'deferred' | 'partial' | 'custom';
  total_amount: number;
  installments: any[];
  start_date: string;
  end_date?: string;
  terms: any;
}

export const usePaymentPlans = (customerId?: string) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const plansQuery = useQuery({
    queryKey: ['payment-plans', customerId, user?.id],
    queryFn: async () => {
      let query = supabase
        .from('payment_plans')
        .select('*')
        .order('created_at', { ascending: false });

      if (customerId) {
        query = query.eq('customer_id', customerId);
      }

      const { data, error } = await query;
      
      if (error) throw error;
      return data as PaymentPlan[];
    },
    enabled: !!user?.id,
  });

  const createPlanMutation = useMutation({
    mutationFn: async (planData: CreatePaymentPlanData) => {
      const { data, error } = await supabase
        .from('payment_plans')
        .insert([{
          ...planData,
          user_id: user?.id,
          status: 'pending',
        }])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-plans'] });
      toast({
        title: "Payment plan created",
        description: "Payment plan has been created successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to create payment plan",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updatePlanMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<CreatePaymentPlanData> }) => {
      const { data, error } = await supabase
        .from('payment_plans')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-plans'] });
      toast({
        title: "Payment plan updated",
        description: "Payment plan has been updated successfully.",
      });
    },
  });

  return {
    plans: plansQuery.data || [],
    isLoading: plansQuery.isLoading,
    error: plansQuery.error,
    createPlan: createPlanMutation.mutate,
    updatePlan: updatePlanMutation.mutate,
    isCreating: createPlanMutation.isPending,
    isUpdating: updatePlanMutation.isPending,
  };
};